import type { Meta } from 'app-components/flux/core/actionHelpers';
import {
  type AccountPreferencesById,
  type DatasetPreferences,
  type DatasetWebAppPreferences,
  type DatasetWebAppPreferencesInput,
  preferencesActions,
  preferencesSelectors,
  type SetPreferencePayload,
  type UserPreferences,
  type UserWebAppPreferences,
  type UserWebAppPreferencesInput,
} from 'app-components/flux/preferences';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useCallback } from 'react';

type SetPreferenceAction = ReturnType<typeof preferencesActions.setPreference>;

export type QPreferences = {
  setPreference: (payload: SetPreferencePayload, meta?: Meta) => SetPreferenceAction;
  getSharedPreferences: () => void;
  setDatasetPreference: (prefObj: DatasetWebAppPreferencesInput) => void;
  setUserPreference: (prefObj: UserWebAppPreferencesInput) => void;
  datasetPreferences: DatasetWebAppPreferences;
  userPreferences: UserWebAppPreferences | undefined;
  sharedUserPreferences: UserPreferences;
  sharedDatasetPreferences: DatasetPreferences;
  accountPreferencesById: AccountPreferencesById | null | undefined;
  loadPending: boolean;
};

function useQPreferences(): QPreferences {
  const dispatch = useAppDispatch();

  const setPreference = useCallback(
    (payload: SetPreferencePayload, meta?: Meta) => dispatch(preferencesActions.setPreference(payload, meta)),
    [dispatch],
  );

  const getSharedPreferences = useCallback(() => {
    dispatch(preferencesActions.getPreferences({ section: 'shared', group: 'user' }));
    dispatch(preferencesActions.getPreferences({ section: 'shared', group: 'dataset' }));
  }, [dispatch]);

  const setDatasetPreference = useCallback(
    (prefObj: DatasetWebAppPreferencesInput) => {
      dispatch(
        preferencesActions.setPreference(
          {
            section: 'shared',
            group: 'dataset',
            preference: { webApp: prefObj },
          },
          { context: 'QPreferences:setDatasetPreference' },
        ),
      );
    },
    [dispatch],
  );

  const setUserPreference = useCallback(
    (prefObj: UserWebAppPreferencesInput) => {
      dispatch(
        preferencesActions.setPreference(
          {
            section: 'shared',
            group: 'user',
            preference: { webApp: prefObj },
          },
          { context: 'SetupPageAcme:setUserPreference' },
        ),
      );
    },
    [dispatch],
  );

  const loadPending = useAppSelector(preferencesSelectors.getLoadPending);
  const datasetPreferences = useAppSelector((state) =>
    preferencesSelectors.getSharedPreferencesByPath(state, { group: 'dataset', path: ['webApp'] }),
  );
  const userPreferences = useAppSelector((state) =>
    preferencesSelectors.getSharedPreferencesByPath(state, { group: 'user', path: ['webApp'] }),
  );
  const sharedUserPreferences = useAppSelector((state) =>
    preferencesSelectors.getSharedPreferencesByPath(state, { group: 'user' }),
  );
  const sharedDatasetPreferences = useAppSelector((state) =>
    preferencesSelectors.getSharedPreferencesByPath(state, { group: 'dataset' }),
  );
  const accountPreferencesById = useAppSelector((state) => preferencesSelectors.accountPreferencesById(state));

  return {
    setPreference,
    getSharedPreferences,
    setDatasetPreference,
    setUserPreference,
    datasetPreferences,
    userPreferences,
    sharedUserPreferences,
    sharedDatasetPreferences,
    accountPreferencesById,
    loadPending,
  };
}

export default useQPreferences;
